import { CheckCircleIcon, AlertTriangleIcon, XCircleIcon, InfoIcon, XIcon } from 'lucide-react'
import React from 'react'

export type AlertModalProps = {
  isOpen: boolean
  onClose: () => void
  title: string
  message: string
  variant?: 'success' | 'error' | 'warning' | 'info'
  buttonText?: string
}

const AlertModal: React.FC<AlertModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  variant = 'info',
  buttonText = 'OK',
}) => {
  if (!isOpen) return null

  const variants = {
    success: {
      icon: CheckCircleIcon,
      iconColor: 'text-green-500',
      titleColor: 'text-green-800',
      buttonBg: 'bg-green-600 hover:bg-green-700',
      borderColor: 'border-green-200',
    },
    error: {
      icon: XCircleIcon,
      iconColor: 'text-red-500',
      titleColor: 'text-red-800',
      buttonBg: 'bg-red-600 hover:bg-red-700',
      borderColor: 'border-red-200',
    },
    warning: {
      icon: AlertTriangleIcon,
      iconColor: 'text-yellow-500',
      titleColor: 'text-yellow-800',
      buttonBg: 'bg-yellow-600 hover:bg-yellow-700',
      borderColor: 'border-yellow-200',
    },
    info: {
      icon: InfoIcon,
      iconColor: 'text-blue-500',
      titleColor: 'text-blue-800',
      buttonBg: 'bg-blue-600 hover:bg-blue-700',
      borderColor: 'border-blue-200',
    },
  }

  const config = variants[variant]
  const IconComponent = config.icon

  return (
    <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black">
      {/* Backdrop */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* Modal */}
      <div className="relative mx-4 w-full max-w-md rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4">
          <div className="flex items-center space-x-3">
            <IconComponent className={`h-6 w-6 ${config.iconColor}`} />
            <h3 className={`text-lg font-semibold ${config.titleColor}`}>{title}</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 transition-colors hover:text-gray-600">
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          <p className="whitespace-pre-line text-gray-600">{message}</p>
        </div>

        {/* Actions */}
        <div className={`border-t p-6 pt-4 ${config.borderColor}`}>
          <button
            onClick={onClose}
            className={`w-full px-4 py-2 text-sm font-medium text-white ${config.buttonBg} focus:ring-opacity-50 rounded-md border border-transparent focus:ring-2 focus:ring-offset-2 focus:outline-none`}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AlertModal
